import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"relative inline-flex shrink-0 items-center justify-center gap-2 overflow-hidden rounded-[2px] border font-mono text-[0.8rem] font-medium tracking-[0.02em] outline-none transition-[color,background-color,background-size,border-color,translate] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:translate-y-px [&_svg]:transition-[translate] [&_svg]:duration-300 [&_svg]:ease-[cubic-bezier(0.22,1,0.36,1)] hover:[&_.lucide-arrow-up-right]:translate-x-0.5 hover:[&_.lucide-arrow-up-right]:-translate-y-0.5 hover:[&_.lucide-arrow-down]:translate-y-0.5 hover:[&_.lucide-arrow-right]:translate-x-1 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
	{
		variants: {
			variant: {
				default:
					"border-ink bg-ink text-paper after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:origin-left after:scale-x-0 after:bg-[image:var(--pastel)] after:transition-transform after:duration-500 after:ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-tone-2 hover:bg-tone-2 hover:after:scale-x-100",
				destructive:
					"border-destructive bg-destructive text-destructive-foreground hover:opacity-90",
				outline:
					"border-ink bg-transparent bg-[linear-gradient(var(--ink),var(--ink))] bg-[length:0%_100%] bg-left bg-no-repeat text-ink hover:bg-[length:100%_100%] hover:text-paper",
				secondary: "border-rule-2 bg-paper-2 text-ink hover:border-ink",
				ghost:
					"border-transparent bg-transparent text-ink-2 hover:bg-paper-3 hover:text-ink",
				link: "border-transparent bg-transparent text-link underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 gap-1.5 px-3 text-xs has-[>svg]:px-2.5",
				lg: "h-12 px-6 text-sm has-[>svg]:px-5",
				icon: "size-9",
				"icon-sm": "size-8",
				"icon-lg": "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant = "default",
	size = "default",
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
